// Videoaulas gravadas do Ensino: MP4/WebM/MOV no bucket privado `ensino-videos`
// (migration 031). A aula guarda só { path, nome, tamanho, tipo } em `video`;
// para assistir, gera URL assinada.
//
// O envio é retomável (TUS) em pedaços de 6 MB — o tamanho que o Storage da
// Supabase exige no TUS. Arquivo de dezenas/centenas de MB não cabe num POST
// único com segurança, e a conexão caindo no meio não perde o que já subiu.

import * as tus from 'tus-js-client';
import { supabase } from './supabase';

// Sem teto de tamanho na tela nem no bucket (Bianca, 28/10/2026: "não quero
// limite"). O global do projeto foi para 500 GB, o máximo do plano.
const BUCKET = 'ensino-videos';
// URL assinada longa o bastante para uma aula inteira sem a reprodução cair.
const SIGNED_URL_TTL = 4 * 60 * 60;
const TIPOS = {
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
};

function sanitizar(nome) {
  return nome.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
}

// Alguns navegadores no Windows deixam file.type vazio para .mov/.mp4:
// cai para a extensão.
function tipoDoArquivo(file) {
  if (TIPOS[file.type]) return file.type;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (ext === 'mp4' || ext === 'm4v') return 'video/mp4';
  if (ext === 'webm') return 'video/webm';
  if (ext === 'mov') return 'video/quicktime';
  return null;
}

// Endpoint TUS no host direto do Storage ({ref}.storage.supabase.co), como a
// Supabase recomenda para arquivo grande.
function endpointTus() {
  const base = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/$/, '');
  const m = base.match(/^https:\/\/([a-z0-9]+)\.supabase\.co$/);
  return m ? `https://${m[1]}.storage.supabase.co/storage/v1/upload/resumable` : `${base}/storage/v1/upload/resumable`;
}

function mensagemDeErro(err) {
  const status = err?.originalResponse?.getStatus?.();
  const corpo = err?.originalResponse?.getBody?.() || '';
  if (status === 413 || /maximum allowed size|Payload too large|exceeded/i.test(corpo)) {
    return 'O armazenamento recusou o tamanho do vídeo. Fale com a administração do sistema.';
  }
  if (status === 415 || /mime type/i.test(corpo)) return 'Formato não aceito. Envie o vídeo em MP4, WebM ou MOV.';
  if (status === 401 || status === 403) return 'Sem permissão para enviar vídeos. Apenas administradores e instrutores podem anexar.';
  return 'Não foi possível enviar o vídeo. Verifique a conexão e tente novamente.';
}

/**
 * Envia o vídeo da aula. `aoProgredir(percentual)` recebe 0–100.
 * Devolve { promessa, cancelar } — cancelar interrompe o envio.
 */
export function uploadVideoAula(file, aulaId, aoProgredir) {
  let upload = null;
  let cancelado = false;
  let rejeitar = null;
  const promessa = (async () => {
    if (!file) throw new Error('Selecione o arquivo de vídeo.');
    const tipo = tipoDoArquivo(file);
    if (!tipo) throw new Error('Formato não aceito. Envie o vídeo em MP4, WebM ou MOV.');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada. Entre novamente para enviar o vídeo.');

    const id = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const path = `aulas/${aulaId}/${id}-${sanitizar(file.name)}`;

    if (cancelado) throw new Error('Envio cancelado.');
    await new Promise((resolve, reject) => {
      // O tus não chama onError no abort: o cancelar rejeita por aqui.
      rejeitar = reject;
      upload = new tus.Upload(file, {
        endpoint: endpointTus(),
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${session.access_token}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
          'x-upsert': 'false',
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        metadata: { bucketName: BUCKET, objectName: path, contentType: tipo, cacheControl: '3600' },
        chunkSize: 6 * 1024 * 1024,
        onError: (err) => reject(new Error(cancelado ? 'Envio cancelado.' : mensagemDeErro(err))),
        onProgress: (enviados, total) => aoProgredir?.(total ? Math.round((enviados / total) * 100) : 0),
        onSuccess: () => resolve(),
      });
      // Envio interrompido do mesmo arquivo (queda de conexão, F5): continua de onde parou.
      upload.findPreviousUploads()
        .then(anteriores => { if (anteriores.length) upload.resumeFromPreviousUpload(anteriores[0]); })
        .catch(() => {})
        .finally(() => { if (!cancelado) upload.start(); });
    });
    return { path, nome: file.name, tamanho: file.size, tipo };
  })();
  const cancelar = () => {
    cancelado = true;
    upload?.abort(true).catch(() => {});
    rejeitar?.(new Error('Envio cancelado.'));
  };
  return { promessa, cancelar };
}

export async function urlVideoAula(path) {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, SIGNED_URL_TTL);
  if (error) throw new Error('Não foi possível carregar o vídeo.');
  return data.signedUrl;
}

export async function excluirVideoAula(path) {
  if (!path) return;
  await supabase.storage.from(BUCKET).remove([path]);
}
